import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button, Space, Typography, Table, Input, Modal, Dropdown, Tooltip, Empty } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faSearch } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import _ from "lodash";

const NEW_BILLING_GROUP_BASE_STATE = { name: "", items: [] };
const NEW_BILLING_RATE_BASE_STATE = { item: null, items_xref: null };

function BillingGroups(props) {
  const [billingGroups, setBillingGroups] = useState([]);
  const [filteredBillingGroups, setFilteredBillingGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState(null);

  const [selectedBillingGroup, setSelectedBillingGroup] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newBillingGroup, setNewBillingGroup] = useState(NEW_BILLING_GROUP_BASE_STATE);
  const [modalWidth, setModalWidth] = useState(1200);
  const [billRateToAdd, setBillRateToAdd] = useState(NEW_BILLING_RATE_BASE_STATE);

  const [duplicationModalOpen, setDuplicationModalOpen] = useState(false);

  const [items, setItems] = useState([]);

  const table_columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <Typography.Title level={5}>{text}</Typography.Title>,
      sorter: (a, b) => (a.name > b.name ? 1 : -1),
      defaultSortOrder: "ascend",
    },
    {
      title: "Users In This Group",
      dataIndex: "users_list",
      key: "users_list",
      render: (text) => <Tooltip title={text}>{_.truncate(text, { separator: ", ", length: 128 })}</Tooltip>,
    },
    {
      title: "Items Covered By This Group",
      dataIndex: "items_list",
      key: "items_list",
      render: (text) => <Tooltip title={text}>{_.truncate(text, { separator: ", ", length: 128 })}</Tooltip>,
    },
    {
      title: "Actions",
      align: "right",
      dataIndex: "id",
      key: "id",
      render: (text, row) => (
        <Space>
          <Button
            size="small"
            type="primary"
            onClick={() => {
              setSelectedBillingGroup({ ...billingGroups.find((f) => f.id === text) });
              setEditModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faEdit} className="mr-1" />
            Edit
          </Button>
          <Button
            size="small"
            danger
            onClick={() => {
              setDeleteModalOpen(true);
              setSelectedBillingGroup(billingGroups.find((f) => f.id === text));
            }}
          >
            <FontAwesomeIcon icon={faTrash} className="mr-1" />
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const GetBillingGroups = async () => {
    let response = await props.AxiosRequest("/api/billing-groups/get");
    setBillingGroups(
      response.data.billing_groups
        .map((m) => {
          return { ...m, value: m.id, label: m.name };
        })
        .sort((a, b) => {
          return ("" + a.name).localeCompare(b.name);
        }),
    );
    setFilteredBillingGroups(response.data.billing_groups);
  };
  const UpdateBillingGroup = async () => {
    if (selectedBillingGroup.name.length === 0) return props.MakeNotification("info", "Please Enter All Required Fields.");
    await props.AxiosRequest("/api/billing-groups/update", { ...selectedBillingGroup, items: selectedBillingGroup.items.filter((f) => f.bill_rate) });
    GetBillingGroups();
    setEditModalOpen(false);
    setSelectedBillingGroup(null);
  };
  const DuplicateBillingGroup = async (id) => {
    if (!selectedBillingGroup) return props.MakeNotification("warning", "Please select a billing group to duplicate.");
    await props.AxiosRequest("/api/billing-groups/duplicate", { id: selectedBillingGroup.id });
    GetBillingGroups();
    setDuplicationModalOpen(false);
    setSelectedBillingGroup(null);
  };
  const DeleteBillingGroup = async () => {
    await props.AxiosRequest("/api/billing-groups/delete", { id: selectedBillingGroup.id });
    GetBillingGroups();
    setDeleteModalOpen(false);
    setSelectedBillingGroup(null);
  };
  const AddBillingGroup = async () => {
    if (newBillingGroup.name.length === 0) return props.MakeNotification("info", "Please Enter All Required Fields.");
    let all_items_have_bill_rates = true;
    newBillingGroup.items.forEach((m) => {
      if (!m.bill_rate || isNaN(m.bill_rate)) all_items_have_bill_rates = false;
    });
    if (!all_items_have_bill_rates) return props.MakeNotification("info", "Please Enter Valid Bill Rates For All Items In This Billing Group.");

    await props.AxiosRequest("/api/billing-groups/insert", { ...newBillingGroup });
    GetBillingGroups();
    setAddModalOpen(false);
    setNewBillingGroup(NEW_BILLING_GROUP_BASE_STATE);
  };

  const GetItems = async () => {
    let response = await props.AxiosRequest("/api/items/get");
    setItems(
      response.data.items.map((m) => {
        return { ...m, label: m.name, value: m.id };
      }),
    );
  };

  const dropdownMenuItems = [
    {
      label: (
        <a
          onClick={() => {
            setNewBillingGroup(NEW_BILLING_GROUP_BASE_STATE);
            setAddModalOpen(true);
          }}
        >
          Create a New Billing Group From Scratch
        </a>
      ),
      key: "0",
    },
    {
      label: (
        <a
          onClick={() => {
            setDuplicationModalOpen(true);
          }}
        >
          Duplicate an Existing Billing Group
        </a>
      ),
      key: "1",
    },
  ];

  useEffect(() => {
    GetBillingGroups();
    GetItems();
  }, []);

  useEffect(() => {
    if (searchTerm?.length === 0) return setFilteredBillingGroups(billingGroups);
    let filtered_list = billingGroups.filter((f) => (_.toLower(f.name).indexOf(_.toLower(searchTerm)) > -1 ? true : false));
    setFilteredBillingGroups(filtered_list);
  }, [searchTerm]);

  useEffect(() => {
    const resize = () => {
      const maxSize = Math.min(1200, window.innerWidth);
      setModalWidth(maxSize);
    };
    window.addEventListener("resize", resize);
  });

  return (
    <main className="content">
      <div className="container-fluid">
        <div className="header">
          <h1 className="header-title">Billing Groups</h1>
        </div>
        <Row>
          <Card
            title={<>Billing Groups</>}
            style={{
              width: "100%",
            }}
            extra={
              <>
                <Dropdown
                  trigger={"click"}
                  menu={{
                    items: dropdownMenuItems,
                    // onClick: (item) => {
                    //   DuplicateBillingGroup(item.key);
                    // },
                  }}
                >
                  <Button type="primary">
                    <FontAwesomeIcon icon="plus" className="me-2" /> Add A New Billing Group
                  </Button>
                </Dropdown>
              </>
            }
          >
            <Row>
              <Col span={18}></Col>
              <Col span={6}>
                <Input
                  className="mb-2"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  placeholder="Search..."
                  allowClear
                />
              </Col>
            </Row>
            <Table className="table table-striped hover" columns={table_columns} dataSource={filteredBillingGroups} />
          </Card>
        </Row>
        <Modal
          title="Edit Billing Group"
          centered
          width={modalWidth}
          open={editModalOpen}
          onCancel={() => {
            setEditModalOpen(false);
            setSelectedBillingGroup(null);
          }}
          footer={
            <>
              <Button
                onClick={() => {
                  setEditModalOpen(false);
                  setSelectedBillingGroup(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  UpdateBillingGroup();
                }}
              >
                Save Changes
              </Button>
            </>
          }
        >
          {selectedBillingGroup ? (
            <>
              <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 8]}>
                <Col span={24}>
                  <label className="col-form-label-sm">Billing Group Name *</label>
                  <Input
                    value={selectedBillingGroup.name}
                    onChange={(e) => {
                      setSelectedBillingGroup({ ...selectedBillingGroup, name: e.target.value });
                    }}
                  />
                </Col>
                <Col span={24}>
                  <hr />
                </Col>
                <Col span={12}>
                  <label className="col-form-label-sm">Select An Item To Add</label>
                  <Select
                    value={billRateToAdd.item}
                    options={items}
                    onChange={(e) => {
                      if (selectedBillingGroup.items.map((m) => m.items_xref).includes(e.value)) return props.MakeNotification("info", "That Item Is Already In This Billing Group.");
                      setSelectedBillingGroup({ ...selectedBillingGroup, items: [...selectedBillingGroup.items, { items_xref: e.value, item: e }] });
                      setBillRateToAdd(NEW_BILLING_RATE_BASE_STATE);
                    }}
                  />
                </Col>
                <Col span={12} style={{ maxHeight: "55vh", overflow: "scroll", overflowY: "scroll" }}>
                  {selectedBillingGroup.items.length > 0 ? (
                    <>
                      <Row gutter={[{ xs: 8, sm: 16, md: 16, lg: 16 }, 8]}>
                        <Col span={10}>
                          <label className="col-form-label-sm">Item *</label>
                        </Col>
                        <Col span={10}>
                          <label className="col-form-label-sm">Billing Rate *</label>
                        </Col>
                        <Col span={4}></Col>
                      </Row>
                      <Row gutter={[{ xs: 8, sm: 16, md: 16, lg: 16 }, 8]}>
                        {selectedBillingGroup.items.map((m) => {
                          return (
                            <>
                              <Col span={10}>
                                <Input readOnly value={items.find((f) => f.id === m.items_xref)?.name} />
                              </Col>
                              <Col span={10}>
                                <Input
                                  addonBefore="$"
                                  placeholder="99.95, 130, etc."
                                  value={m.bill_rate}
                                  onChange={(e) => {
                                    setSelectedBillingGroup({
                                      ...selectedBillingGroup,
                                      items: selectedBillingGroup.items.map((item) => {
                                        return item.items_xref === m.items_xref ? { ...item, bill_rate: e.target.value } : item;
                                      }),
                                    });
                                  }}
                                />
                              </Col>
                              <Col span={2}>
                                <Tooltip title="Remove item from billing group" placement="right">
                                  <Button
                                    danger
                                    onClick={() => {
                                      setSelectedBillingGroup({ ...selectedBillingGroup, items: selectedBillingGroup.items.filter((f) => f.items_xref !== m.items_xref) });
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                </Tooltip>
                              </Col>
                            </>
                          );
                        })}
                      </Row>
                    </>
                  ) : (
                    <>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={"Select An Item to the Left To Get Started"} />
                    </>
                  )}
                </Col>
              </Row>
            </>
          ) : null}
        </Modal>

        <Modal
          title={"Are you sure?"}
          centered
          open={deleteModalOpen}
          onCancel={() => {
            setDeleteModalOpen(false);
            setSelectedBillingGroup(null);
          }}
          footer={
            <>
              <Button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setSelectedBillingGroup(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                danger
                onClick={() => {
                  DeleteBillingGroup();
                }}
              >
                Delete
              </Button>
            </>
          }
        >
          {selectedBillingGroup ? <>Are you sure you want to delete the billing group {selectedBillingGroup?.name}?</> : null}
        </Modal>

        <Modal
          width={modalWidth}
          title={"Add A New Billing Group"}
          centered
          open={addModalOpen}
          onCancel={() => {
            setAddModalOpen(false);
            setSelectedBillingGroup(null);
          }}
          footer={
            <>
              <Button
                onClick={() => {
                  setAddModalOpen(false);
                  setSelectedBillingGroup(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  AddBillingGroup();
                }}
              >
                Add
              </Button>
            </>
          }
        >
          {newBillingGroup ? (
            <>
              <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 8]}>
                <Col span={24}>
                  <label className="col-form-label-sm">Billing Group Name *</label>
                  <Input
                    value={newBillingGroup.name}
                    placeholder="Partner Billing Group, Associate Billing Group, etc."
                    onChange={(e) => {
                      setNewBillingGroup({ ...newBillingGroup, name: e.target.value });
                    }}
                  />
                </Col>
                <Col span={24}>
                  <hr />
                </Col>
                <Col span={12}>
                  <label className="col-form-label-sm">Select An Item To Add</label>
                  <Select
                    value={billRateToAdd.item}
                    options={items}
                    onChange={(e) => {
                      if (newBillingGroup.items.map((m) => m.items_xref).includes(e.value)) return props.MakeNotification("info", "That Item Is Already In This Billing Group.");
                      setNewBillingGroup({ ...newBillingGroup, items: [...newBillingGroup.items, { items_xref: e.value, item: e }] });
                      setBillRateToAdd(NEW_BILLING_RATE_BASE_STATE);
                    }}
                  />
                </Col>
                <Col span={12} style={{ maxHeight: "55vh", overflow: "scroll", overflowY: "scroll" }}>
                  {newBillingGroup.items.length > 0 ? (
                    <>
                      <Row gutter={[{ xs: 8, sm: 16, md: 16, lg: 16 }, 8]}>
                        <Col span={10}>
                          <label className="col-form-label-sm">Item *</label>
                        </Col>
                        <Col span={10}>
                          <label className="col-form-label-sm">Billing Rate *</label>
                        </Col>
                        <Col span={4}></Col>
                      </Row>
                      <Row gutter={[{ xs: 8, sm: 16, md: 16, lg: 16 }, 8]}>
                        {newBillingGroup.items.map((m) => {
                          return (
                            <>
                              <Col span={10}>
                                <Input readOnly value={items.find((f) => f.id === m.items_xref).name} />
                              </Col>
                              <Col span={10}>
                                <Input
                                  addonBefore="$"
                                  placeholder="99.95, 130, etc."
                                  value={m.bill_rate}
                                  onChange={(e) => {
                                    setNewBillingGroup({
                                      ...newBillingGroup,
                                      items: newBillingGroup.items.map((item) => {
                                        return item.items_xref === m.items_xref ? { ...item, bill_rate: e.target.value } : item;
                                      }),
                                    });
                                  }}
                                />
                              </Col>
                              <Col span={2}>
                                <Tooltip title="Remove item from billing group" placement="right">
                                  <Button
                                    danger
                                    onClick={() => {
                                      setNewBillingGroup({ ...newBillingGroup, items: newBillingGroup.items.filter((f) => f.items_xref !== m.items_xref) });
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                </Tooltip>
                              </Col>
                            </>
                          );
                        })}
                      </Row>
                    </>
                  ) : (
                    <>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={"Select An Item to the Left To Get Started"} />
                    </>
                  )}
                </Col>
              </Row>
            </>
          ) : null}
        </Modal>

        <Modal
          title={"Duplicate An Existing Billing Group"}
          centered
          open={duplicationModalOpen}
          onCancel={() => {
            setDuplicationModalOpen(false);
          }}
          footer={
            <>
              <Button
                onClick={() => {
                  setDuplicationModalOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  DuplicateBillingGroup();
                }}
              >
                Duplicate Billing Group
              </Button>
            </>
          }
        >
          <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 8]}>
            <Col span={24}>
              <Select
                options={billingGroups}
                value={selectedBillingGroup}
                onChange={(e) => {
                  setSelectedBillingGroup(e);
                }}
              />
            </Col>
          </Row>
        </Modal>
      </div>
    </main>
  );
}

export default BillingGroups;
